import React, { useState, useEffect, useMemo } from 'react';
import { COLORS } from '../../utils/constants';
import { tradingAPI } from '../../services/api';

import { Search, ChevronDown, TrendingUp, Loader } from 'lucide-react';

const MarketSelector = ({ selected, onSelect }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSymbols();
  }, []);

  const loadSymbols = async () => {
    try {
      // Load all symbols
      const response = await tradingAPI.getSymbols(5500);
      setSymbols(response.data.symbols || []);
    } catch (error) {
      console.error('Failed to load symbols:', error);
      // Fallback to basic list
      setSymbols([
        { symbol: 'BTCUSDT', name: 'Bitcoin', base: 'BTC' },
        { symbol: 'ETHUSDT', name: 'Ethereum', base: 'ETH' },
        { symbol: 'BNBUSDT', name: 'BNB', base: 'BNB' },
        { symbol: 'SOLUSDT', name: 'Solana', base: 'SOL' },
        { symbol: 'XRPUSDT', name: 'Ripple', base: 'XRP' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Memoized filtered symbols - limit to 100 for performance
  const filteredSymbols = useMemo(() => {
    const searchLower = search.toLowerCase();
    const filtered = symbols.filter(
      (s) =>
        (s.name || '').toLowerCase().includes(searchLower) ||
        s.symbol.toLowerCase().includes(searchLower) ||
        (s.base || '').toLowerCase().includes(searchLower)
    );
    // Limit to 100 results for performance
    return filtered.slice(0, 100);
  }, [symbols, search]);

  const selectedSymbol = symbols.find((s) => s.symbol === selected);

  return (
    <div style={{ position: 'relative' }}>
      {/* Selected Market Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: '12px 16px',
          backgroundColor: COLORS.card,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '8px',
          color: COLORS.text,
          fontSize: '16px',
          fontWeight: 'bold',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minWidth: '220px',
        }}
      >
        <TrendingUp size={18} color={COLORS.primary} />
        <span>{selectedSymbol?.name || selected.replace('USDT', '')}</span>
        <span style={{ color: COLORS.textSecondary, fontSize: '14px', marginLeft: 'auto' }}>
          {selected.replace('USDT', '/USDT')}
        </span>
        <ChevronDown size={16} color={COLORS.textSecondary} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div
            onClick={() => setIsOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
          />

          {/* Dropdown Content */}
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: 0,
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '12px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
              zIndex: 1000,
              minWidth: '350px',
              maxHeight: '500px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header */}
            <div style={{ 
              padding: '16px', 
              borderBottom: `1px solid ${COLORS.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ color: COLORS.text, fontSize: '16px', fontWeight: '600' }}>
                Select Market
              </div>
              <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                {symbols.length} pairs
              </div>
            </div>

            {/* Search */}
            <div style={{ padding: '12px', borderBottom: `1px solid ${COLORS.border}` }}>
              <div style={{ position: 'relative' }}>
                <Search
                  size={16}
                  color={COLORS.textSecondary}
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}
                />
                <input
                  type="text"
                  placeholder="Search coin name or symbol..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 40px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    color: COLORS.text,
                    fontSize: '14px',
                    outline: 'none',
                  }}
                />
              </div>
            </div>

            {/* Markets List */}
            <div style={{ overflowY: 'auto', flex: 1, maxHeight: '350px' }}>
              {loading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textSecondary }}>
                  <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
                  <div style={{ marginTop: '8px' }}>Loading markets...</div>
                </div>
              ) : filteredSymbols.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textSecondary }}>
                  No markets found
                </div>
              ) : (
                filteredSymbols.map((symbol, index) => (
                  <button
                    key={symbol.symbol}
                    onClick={() => {
                      onSelect(symbol.symbol);
                      setIsOpen(false);
                      setSearch('');
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: symbol.symbol === selected ? `${COLORS.primary}15` : 'transparent',
                      border: 'none',
                      borderBottom: index < filteredSymbols.length - 1 ? `1px solid ${COLORS.border}` : 'none',
                      color: COLORS.text,
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (symbol.symbol !== selected) {
                        e.currentTarget.style.backgroundColor = COLORS.background;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (symbol.symbol !== selected) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {/* Coin Icon Placeholder */}
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: `${COLORS.primary}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: COLORS.primary,
                        }}
                      >
                        {symbol.base.slice(0, 2)}
                      </div>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600' }}>{symbol.name}</div>
                        <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                          {symbol.base}/USDT
                        </div>
                      </div>
                    </div>
                    {symbol.symbol === selected && (
                      <div
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: COLORS.primary,
                          color: '#000',
                          fontSize: '10px',
                          fontWeight: '600',
                        }}
                      >
                        SELECTED
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default MarketSelector;
